import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { Point } from "ol/geom";

export type MarkerPoint = {
  x: number;
  y: number;
  index: number;
  time?: number;
};

export type ProfileChartProps = {
  title?: string;
  series: ApexAxisChartSeries;
  markerPoints?: Array<MarkerPoint>;
  onChartMouseEnter: any;
  onChartMouseLeave: any;
  chartId?: string;
};

const ProfileChart: React.FunctionComponent<any> = (
  props: ProfileChartProps
) => {
  const {
    series,
    onChartMouseEnter,
    onChartMouseLeave,
    title,
    markerPoints,
    chartId,
  } = props;
  const [modifiedSeries, setModifiedSeries] = useState<ApexAxisChartSeries>();
  const theme = useTheme();

  const { t } = useTranslation();

  //@ts-ignore
  const seriesStart = series["0"].data[0][0];
  //@ts-ignore
  const seriesEnd = series["0"]?.data[series["0"].data.length - 1][0];
  const seriesDifference = seriesEnd - seriesStart;

  useEffect(() => {
    let newModifiedSeries = series;
    newModifiedSeries["0"].name = t("map:route.graphs.altitude_abbr");
    setModifiedSeries(newModifiedSeries);
  }, [series]);

  function handleZoomLimit(e: any, { xaxis }: any) {
    const zoomDifference = xaxis.max - xaxis.min;
    if (zoomDifference > seriesDifference) {
      return {
        // Dont zoom any more
        xaxis: {
          min: seriesStart,
          max: seriesEnd,
        },
      };
    } else {
      return {
        // Keep on zooming
        xaxis: {
          min: xaxis.min,
          max: xaxis.max,
        },
      };
    }
  }

  const annotations = {
    points: Array.isArray(markerPoints)
      ? markerPoints.map((point: any) => {
          return {
            x: point.x,
            y: point.y,
            // image: {
            //   path: "../images/icons/route_pin.svg",
            //   width: 120,
            //   height: 60
            // },
            label: {
              borderColor: "#005a96",
              borderRadius: "50%",
              text: String(point.index),
              style: {
                color: "#FFFFFF",
                background: "#005a96",
              },
              offsetY: 16,
            },
          };
        })
      : [],
    // xaxis: routePoints && routePoints.length > 2 ? routePoints.map((point: any) => {
    //   return {
    //     x: point.x,
    //     strokeDashArray: 0,
    //     marker: {
    //       size: 8
    //     },
    //     label: {
    //       borderColor: "#000000",
    //       style: {
    //         color: "#fff",
    //         background: "#FF4560"
    //       },
    //       text: String(point.time)
    //     }
    //   }
    // }) : []
  };

  const options = {
    title: {
      text: title,
      align: "center",
      style: {
        color: theme.palette.text.primary,
      },
    },
    chart: {
      id: chartId || "profile-chart",
      fontFamily: "Roboto, Arial, sans-serif",
      width: "100%",
      zoom: {
        enabled: true,
      },
      height: 300,
      type: "area",
      stacked: false,
      toolbar: {
        tools: {
          download: false,
          reset: false,
          selection: false,
          zoom: false,
        },
      },
      events: {
        mouseMove: onChartMouseEnter,
        mouseLeave: onChartMouseLeave,
        beforeZoom: handleZoomLimit,
      },
    },
    annotations: annotations,
    dataLabels: {
      enabled: false,
    },
    colors: ["#005a96", "#005a96", "#005a96"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 1,
        gradientToColors: ["#FAFAFA"],
        inverseColors: false,
        opacityFrom: 0.9,
        opacityTo: 0.4,
        stops: [0, 100],
      },
      colors: ['rgb(199, 252, 150)'],
    },
    stroke: {
      show: true,
      curve: "smooth",
      width: 2,
    },
    yaxis: {
      title: {
        // text: t("map:route.graphs.altitude"),
        style: {
          color: theme.palette.text.primary,
        },
      },
      labels: {
        style: {
          colors: theme.palette.text.primary,
        },
        formatter: (val: number) => {
          return `${val} m`;
        },
        offsetX: 8,
        offsetY: -5,
      },
      tooltip: {
        enabled: false,
      },
    },
    xaxis: {
      type: "numeric",
      min: 0,
      max: seriesEnd,
      floating: false,
      tickAmount: 1,
      title: {
        // text: t("map:route.graphs.distance"),
        style: {
          color: theme.palette.text.primary,
        },
      },
      labels: {
        trim: true,
        style: {
          colors: theme.palette.text.primary,
        },
        formatter: (val: number) => {
          if (seriesEnd > 1000) {
            return `${(val / 1000).toFixed(1)} km`;
          } else {
            return `${Math.round(val)} m`;
          }
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    grid: {
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        left: -30,
        right: 16,
      },
    },
    legend: {
      horizontalAlign: "left",
      offsetX: 40,
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        let title = w.globals.tooltip.tooltipTitle.innerHTML;
        let item = w.globals.tooltip.ttItems[0].querySelector(
          ".apexcharts-tooltip-text-y-value"
        ).textContent;

        return `<div class="custom-chart-tooltip"> ${
          title + " / " + item
        } </div>`;
      },
      marker: {
        show: false,
      },
    },
  };

  /**
     y: {
        //@ts-ignore
        formatter: function(value, { series, seriesIndex, dataPointIndex, w }) {
          return `${value} m`
        }
      },
      marker: {
        show: false
      },
      fixed: {
        enabled: true,
        position: 'topLeft',
        offsetX: 25,
        offsetY: 10,
      },
    }
    }
   */

  return modifiedSeries ? (
    //@ts-ignore
    <Chart
      type="area"
      // @ts-ignore
      options={options}
      series={modifiedSeries}
      height="300px"
    />
  ) : null;
};

export default ProfileChart;
